// shared/anti-autofill.directive.ts
import {
  Directive,
  ElementRef,
  Renderer2,
  HostListener,
  Input,
  OnInit,
} from '@angular/core';

@Directive({
  selector: '[disableAutofill]'
})
export class DisableAutofillDirective implements OnInit {
  /**
   * Optional mode:
   * - 'password' -> use password-specific autocomplete hints
   * - anything else / omitted -> use generic "off"
   */
  @Input('disableAutofill') mode: 'password' | 'off' | '' | null = null;

  private hostEl: HTMLElement;

  constructor(
    private elRef: ElementRef<HTMLElement>,
    private renderer: Renderer2
  ) {
    this.hostEl = this.elRef.nativeElement;
  }

  ngOnInit(): void {
    const tag = this.hostEl.tagName.toLowerCase();

    if (tag === 'form') {
      this.setupForm();
    } else if (tag === 'input' || tag === 'textarea') {
      this.setupInput();
    }
  }

  // -------- FORM SETUP (dummy fields etc.) --------

  private setupForm(): void {
    // Turn off autocomplete for the whole form
    this.renderer.setAttribute(this.hostEl, 'autocomplete', 'off');

    // Create fake username field
    const fakeUser = this.renderer.createElement('input');
    this.renderer.setAttribute(fakeUser, 'type', 'text');
    this.renderer.setAttribute(fakeUser, 'name', 'fake-username');
    this.renderer.setAttribute(fakeUser, 'autocomplete', 'username');
    this.renderer.setAttribute(fakeUser, 'tabindex', '-1');
    this.renderer.setAttribute(fakeUser, 'aria-hidden', 'true');
    this.styleHidden(fakeUser);

    // Create fake password field
    const fakePass = this.renderer.createElement('input');
    this.renderer.setAttribute(fakePass, 'type', 'password');
    this.renderer.setAttribute(fakePass, 'name', 'fake-password');
    this.renderer.setAttribute(fakePass, 'autocomplete', 'new-password');
    this.renderer.setAttribute(fakePass, 'tabindex', '-1');
    this.renderer.setAttribute(fakePass, 'aria-hidden', 'true');
    this.styleHidden(fakePass);

    // Insert them at the top of the form
    const firstChild = this.hostEl.firstChild;
    if (firstChild) {
      this.renderer.insertBefore(this.hostEl, fakeUser, firstChild);
      this.renderer.insertBefore(this.hostEl, fakePass, firstChild);
    } else {
      this.renderer.appendChild(this.hostEl, fakeUser);
      this.renderer.appendChild(this.hostEl, fakePass);
    }
  }

  private styleHidden(el: HTMLElement): void {
    this.renderer.setStyle(el, 'opacity', '0');
    this.renderer.setStyle(el, 'position', 'absolute');
    this.renderer.setStyle(el, 'height', '0');
    this.renderer.setStyle(el, 'width', '0');
    this.renderer.setStyle(el, 'pointer-events', 'none');
    this.renderer.setStyle(el, 'z-index', '-1');
  }

  // -------- INPUT SETUP (autocomplete + readonly trick) --------

  private setupInput(): void {
    const input = this.hostEl as HTMLInputElement;
    const type = (input.getAttribute('type') || '').toLowerCase();

    // Decide autocomplete hint
    const isPassword = this.mode === 'password' || type === 'password';
    const autocompleteValue = isPassword ? 'new-password' : 'off';

    this.renderer.setAttribute(input, 'autocomplete', autocompleteValue);

    // readonly-until-focus trick: discourages some autofill
    this.renderer.setAttribute(input, 'readonly', 'true');
  }

  // Remove readonly on focus so the user can type
  @HostListener('focus')
  handleFocus(): void {
    const tag = this.hostEl.tagName.toLowerCase();
    if (tag === 'input' || tag === 'textarea') {
      const input = this.hostEl as HTMLInputElement;
      this.renderer.removeAttribute(input, 'readonly');
    }
  }

  // Re-apply readonly on blur to keep discouraging autofill
  @HostListener('blur')
  handleBlur(): void {
    const tag = this.hostEl.tagName.toLowerCase();
    if (tag === 'input' || tag === 'textarea') {
      const input = this.hostEl as HTMLInputElement;
      this.renderer.setAttribute(input, 'readonly', 'true');
    }
  }
}
